import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import './scss/createMatch.scss';
import NewLocation from './components/venues/NewLocation';
import AddTeamModal from './components/AddTeamModal';
import SelectOppositionModal from './components/SelectOppositionModal';
import SelectLocationModal from './components/SelectLocationModal';
import api from './services/apiService';
import { useNotification } from './components/NotificationProvider';

function CreateMatch() {
  const [matchDate, setMatchDate] = useState('');
  const [location, setLocation] = useState('');
  const [selectedPitchId, setSelectedPitchId] = useState(null);
  const [matchTime, setMatchTime] = useState('19:30');
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [maxPlayers, setMaxPlayers] = useState(12);
  const [duration, setDuration] = useState(sessionStorage.getItem('selected_team_type') === 'squad' ? 90 : 60);
  const [fee, setFee] = useState(5.00);
  const [feeInput, setFeeInput] = useState("5.00");
  const [pitches, setPitches] = useState([]);
  const [showNewLocation, setShowNewLocation] = useState(false);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [showSelectOpposition, setShowSelectOpposition] = useState(false);
  const [showSelectLocation, setShowSelectLocation] = useState(false);
  const [currentStage, setCurrentStage] = useState(1);
  const [instructions, setInstructions] = useState('');
  const [opposition, setOpposition] = useState('');
  const [oppositionTeams, setOppositionTeams] = useState([]);
  const [customOpposition, setCustomOpposition] = useState('');
  const [teamA, setTeamA] = useState('Whites');
  const [teamB, setTeamB] = useState('Colours');
  const [isHome, setIsHome] = useState(null);
  const [matchType, setMatchType] = useState(sessionStorage.getItem('selected_team_type') === 'friendly' ? 'Friendly' : 'League');
  const [teamSize, setTeamSize] = useState('11v11');
  const [isLoading, setIsLoading] = useState(false);
  const [isTBC, setIsTBC] = useState(false);
  const totalStages = 4;
  const navigate = useNavigate();
  const teamId = sessionStorage.getItem('selected_team_id');
  const teamType = sessionStorage.getItem('selected_team_type');
  const teamName = sessionStorage.getItem('selected_team_name');
  const [dateError, setDateError] = useState('');
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await api.get(process.env.REACT_APP_API_URL + '/get_players.php?team_id=' + teamId);
        const result = response.data;
        if (result.success) {
          setPlayers(result.players);
          // Only auto-select available players (exclude injured/unavailable)
          const availablePlayerIds = result.players
            .filter(player => !player.injured_unavailable_at)
            .map(player => player.player_id);
          setSelectedPlayers(availablePlayerIds);
        } else {
          console.error('Failed to fetch players');
        }
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    };

    const fetchMatches = async () => {
      try {
        const response = await api.get(process.env.REACT_APP_API_URL + '/get_matches_with_invites.php');
        const result = response.data;
        if (result.success) {
          setMatches(result.matches);
        } else {
          console.error('Failed to fetch matches');
        }
      } catch (error) {
        console.error('Error fetching matches:', error);
      }
    };

    const fetchPitches = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/fetch_pitches.php`);
        const result = response.data;
        if (result.success) {
          setPitches(result.pitches);
        } else {
          console.error('Failed to fetch pitches:', result.message);
        }
      } catch (error) {
        console.error('Error fetching pitches:', error);
      }
    };

    const fetchOppositionTeams = async () => {
      if (teamType === 'academy' || teamType === 'squad') {
        try {
          const type = teamType === 'academy' ? 'academy' : 'squad';
          const response = await api.get(`${process.env.REACT_APP_API_URL}/get_teams.php?type=${type}&team_id=${teamId}`);
          const result = response.data;
          if (result.success) {
            setOppositionTeams(result.teams);
          } else {
            console.error('Failed to fetch opposition teams:', result.message);
          }
        } catch (error) {
          console.error('Error fetching opposition teams:', error);
        }
      }
    };

    fetchPlayers();
    fetchMatches();
    fetchPitches();
    fetchOppositionTeams();
  }, [teamId, teamType]);

  useEffect(() => {
    setFeeInput(fee.toFixed(2));
  }, []);

  const handlePlayerSelection = (playerId) => {
    setSelectedPlayers((prevSelected) =>
      prevSelected.includes(playerId)
        ? prevSelected.filter((id) => id !== playerId)
        : [...prevSelected, playerId]
    );
  };

  const handleLocationChange = (event) => {
    const selectedPitchId = event.target.value;
    setSelectedPitchId(selectedPitchId);
    
    // Find the pitch name from the selected pitch ID
    if (selectedPitchId) {
      const selectedPitch = pitches.find(pitch => pitch.pitch_id === parseInt(selectedPitchId));
      if (selectedPitch) {
        setLocation(selectedPitch.name);
      }
    } else {
      setLocation('');
    }
  };

  const handleLocationSelected = (pitch) => {
    const pitchId = typeof pitch.pitch_id === 'string' ? parseInt(pitch.pitch_id) : pitch.pitch_id;
    setSelectedPitchId(pitchId);
    setLocation(pitch.name);
    setShowSelectLocation(false);
  };

  const handleLocationAdded = async (newLocation) => {
    try {
      // The API call is already handled in NewLocation.js, so we just need to update the UI
      // Add the new pitch to the pitches array
      const newPitch = {
        pitch_id: newLocation.pitch_id,
        name: newLocation.name,
        address_1: newLocation.address_1,
        address_2: newLocation.address_2 || '',
        town: newLocation.town,
        county: newLocation.county,
        postcode: newLocation.postcode,
        description: newLocation.description || '',
        facilities: newLocation.facilities || '',
        organiser_contact: newLocation.organiser_contact || ''
      };
      
      setPitches(prevPitches => [...prevPitches, newPitch]);
      const pitchId = typeof newLocation.pitch_id === 'string' ? parseInt(newLocation.pitch_id) : newLocation.pitch_id;
      setSelectedPitchId(pitchId);
      setLocation(newLocation.name);
      setShowNewLocation(false);
    } catch (error) {
      console.error('Error handling location added:', error);
    }
  };

  const handleTeamAdded = async (newTeam) => {
    try {
      // Add the new team to the opposition teams array
      setOppositionTeams(prevTeams => [...prevTeams, newTeam]);
      
      // Set the new team as the selected opposition
      setOpposition(newTeam.team_name);
      setShowAddTeam(false);
      
      showSuccess('Team added successfully!', 3000);
    } catch (error) {
      console.error('Error adding team:', error);
      showError('Error adding team. Please try again.', 5000);
    }
  };

  const handleOppositionSelected = (team) => {
    setOpposition(team.team_name);
    setShowSelectOpposition(false);
  };

  const checkExistingMatch = async (date) => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/check_existing_match.php?team_id=${teamId}&date=${date}`
      );
      const result = response.data;
      
      if (result.success && result.exists) {
        setDateError(result.message);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking existing matches:', error);
      return false;
    }
  };

  const handleDateChange = async (e) => {
    const newDate = e.target.value;
    setMatchDate(newDate);
    
    if (newDate) {
      const hasExisting = await checkExistingMatch(newDate);
      if (hasExisting) {
        // setDateError message is now handled in checkExistingMatch
        // based on the event type returned from the API
      } else {
        setDateError('');
      }
    }
  };

  const renderStageIndicator = () => (
    <div className="stage-indicator">
      {[...Array(totalStages)].map((_, index) => (
        <div 
          key={index} 
          className={`stage ${currentStage === index + 1 ? 'active' : ''} ${currentStage > index + 1 ? 'completed' : ''}`}
        >
          {index + 1}
        </div>
      ))}
    </div>
  );

  const renderStageContent = () => {
    switch (currentStage) {
      case 1:
        return (
          <>
            <h2>Match Details</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="matchDate">Match Date:</label>
                <input
                  type="date"
                  id="matchDate"
                  value={matchDate}
                  onChange={handleDateChange}
                  required
                  className={dateError ? 'error' : ''}
                />
                {dateError && <div className="error-message">{dateError}</div>}
              </div>
              {teamType !== 'academy' && (
                <div className="form-group">
                  <label htmlFor="maxPlayers">Max Players:</label>
                  <input
                    type="number"
                    id="maxPlayers"
                    value={maxPlayers}
                    onChange={(e) => setMaxPlayers(parseInt(e.target.value))}
                    min="1"
                    required
                  />
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="matchTime">Start Time:</label>
                <input
                  type="time"
                  id="matchTime"
                  value={isTBC ? "00:00" : matchTime}
                  onChange={(e) => setMatchTime(e.target.value)}
                  disabled={isTBC}
                  required={!isTBC}
                  style={{ opacity: isTBC ? 0.2 : 1 }}
                />
              </div>
              <div className="form-group">
                <label htmlFor="duration">Duration (mins):</label>
                <input
                  type="number"
                  id="duration"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  min="1"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={isTBC}
                    onChange={(e) => {
                      setIsTBC(e.target.checked);
                      if (e.target.checked) {
                        setMatchTime("00:00");
                      }
                    }}
                  />
                  Start Time TBC (To Be Confirmed)
                </label>
              </div>
            </div>

            {(teamType === 'academy' || teamType === 'squad') && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="matchType">Match Type:</label>
                    <select
                      id="matchType"
                      value={matchType}
                      onChange={(e) => setMatchType(e.target.value)}
                      required
                    >
                      <option value="Friendly">Friendly</option>
                      <option value="League">League</option>
                      <option value="Cup">Cup</option>
                      <option value="Tournament">Tournament</option>
                    </select>
                  </div>

                </div>
              </>
            )}

            {/* Only show match fee for non-academy teams */}
            {teamType !== 'academy' && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fee">Match Fee (£):</label>
                  <input
                    type="text"
                    id="fee"
                    value={feeInput}
                    onChange={(e) => {
                      // Store the raw input
                      const rawInput = e.target.value.replace(/[^\d.]/g, '');
                      setFeeInput(rawInput);
                      
                      if (rawInput === '') {
                        setFee(0);
                        return;
                      }
                      
                      // If input contains a decimal point, parse directly
                      if (rawInput.includes('.')) {
                        const numValue = parseFloat(rawInput);
                        if (!isNaN(numValue)) {
                          setFee(numValue);
                        }
                      } else {
                        // Convert whole numbers based on input length
                        if (rawInput.length >= 3) {
                          // Convert "550" to 5.50, "1250" to 12.50
                          const pounds = rawInput.slice(0, -2);
                          const pence = rawInput.slice(-2);
                          const decimalValue = parseFloat(`${pounds}.${pence}`);
                          setFee(decimalValue);
                        } else {
                          // For 1-2 digits, just use the actual value
                          setFee(parseFloat(rawInput));
                        }
                      }
                    }}
                    onBlur={() => {
                      // Format the display value on blur
                      setFeeInput(fee.toFixed(2));
                    }}
                    onFocus={(e) => {
                      // On focus, if the value is "0.00", clear it for easier editing
                      if (parseFloat(e.target.value) === 0) {
                        setFeeInput('');
                      }
                    }}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
            )}
          </>
        );

      case 2:
        return (
          <>
            <h2>Teams & Location</h2>
            {teamType === 'friendly' ? (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="teamA">Team A:</label>
                  <input
                    type="text"
                    id="teamA"
                    value={teamA}
                    onChange={(e) => setTeamA(e.target.value)}
                    placeholder="Whites"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="teamB">Team B:</label>
                  <input
                    type="text"
                    id="teamB"
                    value={teamB}
                    onChange={(e) => setTeamB(e.target.value)}
                    placeholder="Colours"
                    required
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <div className="label-with-button">
                    <label htmlFor="opposition">Opposition:</label>
                    <button 
                      type="button" 
                      className="add-team-button"
                      onClick={() => setShowAddTeam(true)}
                    >
                      + Add Team
                    </button>
                  </div>
                  {oppositionTeams.length > 0 ? (
                    <>
                      <button
                        type="button"
                        className="select-opposition-button"
                        onClick={() => setShowSelectOpposition(true)}
                      >
                        {opposition || '-- Select opposition team --'}
                      </button>
                      {opposition && (
                        <div className="selected-opposition-info">
                          {oppositionTeams.find(t => t.team_name === opposition)?.age_group && (
                            <span className="info-text">
                              Age Group: {oppositionTeams.find(t => t.team_name === opposition).age_group}
                            </span>
                          )}
                          {oppositionTeams.find(t => t.team_name === opposition)?.league_name && (
                            <span className="info-text">
                              League: {oppositionTeams.find(t => t.team_name === opposition).league_name}
                            </span>
                          )}
                          {oppositionTeams.find(t => t.team_name === opposition)?.manager_name && (
                            <span className="info-text manager">
                              Manager: {oppositionTeams.find(t => t.team_name === opposition).manager_name}
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <input
                      type="text"
                      id="opposition"
                      value={opposition}
                      onChange={(e) => setOpposition(e.target.value)}
                      placeholder="Enter opposition team name"
                      required
                    />
                  )}
                  {opposition === 'other' && (
                    <input
                      type="text"
                      id="custom-opposition"
                      value={customOpposition}
                      onChange={(e) => setCustomOpposition(e.target.value)}
                      placeholder="Enter custom team name"
                      required
                      style={{ marginTop: '10px' }}
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Match Location:</label>
                  <div className="radio-group">
                    <label className={`radio-label ${isHome === true ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="isHome"
                        value="home"
                        checked={isHome === true}
                        onChange={() => setIsHome(true)}
                        required
                      />
                      Home
                    </label>
                    <label className={`radio-label ${isHome === false ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="isHome"
                        value="away"
                        checked={isHome === false}
                        onChange={() => setIsHome(false)}
                        required
                      />
                      Away
                    </label>
                  </div>
                </div>
                
              </>
            )}
            <div className="form-group location-group">
                  <div className="label-with-button">
                    <label htmlFor="location">Location:</label>
                    <button 
                      type="button" 
                      className="add-location-button"
                      onClick={() => setShowNewLocation(true)}
                    >
                      + Add New
                    </button>
                  </div>
                  {pitches.length > 0 ? (
                    <>
                      <button
                        type="button"
                        className="select-location-button"
                        onClick={() => setShowSelectLocation(true)}
                      >
                        {(() => {
                          if (!selectedPitchId) return '-- Select a Pitch --';
                          const selectedPitch = pitches.find(p => {
                            const pId = typeof p.pitch_id === 'string' ? parseInt(p.pitch_id) : p.pitch_id;
                            const sId = typeof selectedPitchId === 'string' ? parseInt(selectedPitchId) : selectedPitchId;
                            return pId === sId;
                          });
                          return selectedPitch?.name || '-- Select a Pitch --';
                        })()}
                      </button>
                      {selectedPitchId && (
                        <div className="selected-location-info">
                          {(() => {
                            const selectedPitch = pitches.find(p => {
                              const pId = typeof p.pitch_id === 'string' ? parseInt(p.pitch_id) : p.pitch_id;
                              const sId = typeof selectedPitchId === 'string' ? parseInt(selectedPitchId) : selectedPitchId;
                              return pId === sId;
                            });
                            if (!selectedPitch) {
                              return <span className="info-text">Location selected</span>;
                            }
                            return (
                              <>
                                {selectedPitch.town && selectedPitch.county && (
                                  <span className="info-text">
                                    {selectedPitch.town}, {selectedPitch.county}
                                  </span>
                                )}
                                {selectedPitch.postcode && (
                                  <span className="info-text">
                                    {selectedPitch.postcode}
                                  </span>
                                )}
                                {!selectedPitch.town && !selectedPitch.county && !selectedPitch.postcode && (
                                  <span className="info-text">Location selected</span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </>
                  ) : (
                    <input
                      type="text"
                      id="location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Enter location"
                      required
                    />
                  )}
                </div>
          </>
        );

      case 3:
        return (
          <>
            <h2>Match Instructions</h2>
            <div className="form-group">
              <label htmlFor="instructions">Additional Instructions:</label>
              <textarea
                id="instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Add any specific instructions for players..."
                rows="4"
              />
            </div>
          </>
        );

      case 4:
        const availablePlayers = players.filter(player => !player.injured_unavailable_at);
        const unavailablePlayers = players.filter(player => player.injured_unavailable_at);
        
        return (
          <>
            <h2>Invite Players</h2>
            <button 
              type="button" 
              className="select-all-button"
              onClick={() => {
                const allPlayerIds = availablePlayers.map(player => player.player_id);
                setSelectedPlayers(selectedPlayers.length === availablePlayers.length ? [] : allPlayerIds);
              }}
            >
              {selectedPlayers.length === availablePlayers.length ? 'Deselect All' : 'Select All'}
            </button>
            <div className="players-list">
              {availablePlayers.map((player) => (
                <div key={player.player_id} className="player-item">
                  <input
                    type="checkbox"
                    id={`player-${player.player_id}`}
                    checked={selectedPlayers.includes(player.player_id)}
                    onChange={() => handlePlayerSelection(player.player_id)}
                  />
                  <label htmlFor={`player-${player.player_id}`}>
                    {player.first_name?.charAt(0).toUpperCase() + player.first_name?.slice(1).toLowerCase()}{' '}
                    {player.last_name?.charAt(0).toUpperCase() + player.last_name?.slice(1).toLowerCase()}
                  </label>
                </div>
              ))}
            </div>
            {availablePlayers.length === 0 ? (
              <p className="selection-required">You need to invite players to your team first. A minimum of 6 players is required to create a match.</p>
            ) : selectedPlayers.length === 0 ? (
              <p className="selection-required">Please select at least 6 players to create a match</p>
            ) : null}
          </>
        );

      default:
        return null;
    }
  };

  const handleNext = async () => {
    if (currentStage === 1) {
      const hasExisting = await checkExistingMatch(matchDate);
      if (hasExisting) {
        return;
      }
    }
    
    if (currentStage < totalStages) {
      setCurrentStage(currentStage + 1);
    }
  };

  const handleBack = () => {
    if (currentStage > 1) {
      setCurrentStage(currentStage - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentStage < totalStages) {
      await handleNext();
    } else {
      // Validate that at least 6 players are selected
      if (selectedPlayers.length < 6) {
        showError('Please select at least 6 players to create a match', 5000);
        return;
      }
      
      setIsLoading(true);
      
      try {
        const finalOpposition = opposition === 'other' ? customOpposition : opposition;
        
        // Find the selected opposition team ID if a team was selected from the dropdown
        let oppositionTeamId = null;
        if (teamType !== 'friendly' && opposition !== 'other' && opposition !== '') {
          const selectedTeam = oppositionTeams.find(team => team.team_name === opposition);
          oppositionTeamId = selectedTeam ? selectedTeam.team_id : null;
        }
        
        const response = await api.post(process.env.REACT_APP_API_URL + '/create_match.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            match_date: matchDate,
            location: location,
            pitch_id: selectedPitchId,
            match_time: matchTime,
            players: selectedPlayers,
            max_players: maxPlayers,
            duration: duration,
            fee: fee.toFixed(2),
            team_id: teamId,
            team_name: teamName,
            instructions: instructions,
            opposition: teamType === 'friendly' ? `${teamA} vs ${teamB}` : finalOpposition,
            opposition_team_id: oppositionTeamId,
            team_type: teamType,
            team_a: teamType === 'friendly' ? teamA : null,
            team_b: teamType === 'friendly' ? teamB : null,
            is_home: isHome === true ? 1 : isHome === false ? 0 : null,
            match_type: teamType === 'academy' ? matchType : null,
            custom_opposition: customOpposition,
            team_size: teamType === 'academy' ? teamSize : null,
            isTBC: isTBC
          })
        });

        const result = response.data;
        if (result.success) {
          showSuccess('Match created successfully! Players have been notified.', 5000);
          navigate('/dashboard');
        } else {
          showError(`Failed to create match: ${result.message || 'Unknown error'}`, 5000);
          console.error('Failed to create match:', result.message);
        }
      } catch (error) {
        showError('Error creating match. Please try again.', 5000);
        console.error('Error creating match:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="CreateMatchForm container">
      <Header />
      <button className="back-button" onClick={() => window.history.back()}>← Back</button>
      <h1>Create a New Match</h1>
      {renderStageIndicator()}
      <form onSubmit={handleSubmit}>
        {renderStageContent()}
        {currentStage === 4 && players.filter(player => player.injured_unavailable_at).length > 0 && (
          <div className="unavailable-players-section">
            <h3>Unavailable Players</h3>
            <div className="unavailable-players-list">
              {players
                .filter(player => player.injured_unavailable_at)
                .map((player) => (
                  <div key={player.player_id} className="unavailable-player-item">
                    <span className="unavailable-player-name">
                      {player.first_name} {player.last_name}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
        <div className="form-navigation">
          {currentStage > 1 && (
            <button 
              type="button" 
              onClick={handleBack} 
              className="back-stage-button"
              disabled={isLoading}
            >
              Previous
            </button>
          )}
          <button 
            type="submit" 
            className="next-stage-button"
            disabled={(currentStage === totalStages && selectedPlayers.length === 0) || isLoading}
          >
            {isLoading ? (
              <span className="loading-spinner">
                Creating match...
              </span>
            ) : (
              currentStage === totalStages ? 'Create' : 'Next'
            )}
          </button>
        </div>
      </form>

      {showNewLocation && (
        <NewLocation 
          onClose={() => setShowNewLocation(false)}
          onLocationAdded={handleLocationAdded}
        />
      )}

      {showAddTeam && (
        <AddTeamModal 
          onClose={() => setShowAddTeam(false)}
          onTeamAdded={handleTeamAdded}
          teamType={teamType}
          currentTeam={{
            age_group: sessionStorage.getItem('selected_team_age_group'),
            league_name: sessionStorage.getItem('selected_team_league')
          }}
        />
      )}

      {showSelectOpposition && oppositionTeams.length > 0 && (
        <SelectOppositionModal
          teams={oppositionTeams}
          onClose={() => setShowSelectOpposition(false)}
          onSelectTeam={handleOppositionSelected}
          currentTeamAgeGroup={sessionStorage.getItem('selected_team_age_group')}
          currentTeamLeague={sessionStorage.getItem('selected_team_league')}
          teamType={teamType}
        />
      )}

      {showSelectLocation && pitches.length > 0 && (
        <SelectLocationModal
          pitches={pitches}
          onClose={() => setShowSelectLocation(false)}
          onSelectLocation={handleLocationSelected}
        />
      )}
    </div>
  );
}

export default CreateMatch;